import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AudioTranscriptionBatchStatus } from '@shared/schemas/audio-transcription';
import { conversationKeys } from './useConversation';
import { conversationChatRegistry } from '../chat-stream-registry';
import { isAudioTranscriptionTerminalStatus } from '../audio-transcription-ui';
import { getConversation } from '../services/chat.api';
import {
  cancelAudioTranscriptionBatch,
  getAudioTranscriptionBatch,
  getAudioTranscript,
  retryAudioTranscriptionAnalysis,
  retryAudioTranscriptionJob,
} from '../services/audio-transcription.api';
import { syncConversationDetailCache } from './chat-session-cache';
import { toChatUiMessages } from '../types';

const AUDIO_POLL_BASE_INTERVAL_MS = 2_000;
const AUDIO_POLL_SLOW_INTERVAL_MS = 5_000;
const AUDIO_POLL_SLOWDOWN_AFTER_UPDATES = 60;
const AUDIO_POLL_MAX_UPDATES = 200;
const AUDIO_POLL_MAX_CONSECUTIVE_FAILURES = 5;

export const audioTranscriptionBatchQueryKey = (batchId: string) => (
  ['audio-transcription-batch', batchId] as const
);

export const audioTranscriptQueryKey = (batchId: string, jobId: string) => (
  ['audio-transcription-transcript', batchId, jobId] as const
);

export function resolveAudioTranscriptionPollInterval(input: {
  dataUpdateCount: number;
  fetchFailureCount: number;
  status: AudioTranscriptionBatchStatus | string | undefined;
}): number | false {
  if (isAudioTranscriptionTerminalStatus(input.status)) {
    return false;
  }
  if (input.fetchFailureCount >= AUDIO_POLL_MAX_CONSECUTIVE_FAILURES) {
    return false;
  }
  if (input.dataUpdateCount >= AUDIO_POLL_MAX_UPDATES) {
    return false;
  }
  return input.dataUpdateCount >= AUDIO_POLL_SLOWDOWN_AFTER_UPDATES
    ? AUDIO_POLL_SLOW_INTERVAL_MS
    : AUDIO_POLL_BASE_INTERVAL_MS;
}

export function reconcileLocalMessagesById<T extends { id: string }>(
  current: T[],
  server: T[],
): T[] {
  const serverById = new Map(server.map((message) => [message.id, message]));
  return current.map((message) => serverById.get(message.id) ?? message);
}

async function reconcileConversationAfterTerminal(conversationId: string, queryClient: QueryClient) {
  const payload = await getConversation(conversationId);
  const messages = toChatUiMessages(payload);
  conversationChatRegistry.getConversationEntry(conversationId)?.replaceMessages((current) => (
    reconcileLocalMessagesById(current, messages)
  ));
  syncConversationDetailCache(queryClient, conversationId, messages, payload.conversation);
  await queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversationId) });
}

export function useAudioTranscription(batchId: string | undefined) {
  const queryClient = useQueryClient();
  const didReconcileRef = useRef(false);

  const query = useQuery({
    enabled: Boolean(batchId),
    queryFn: () => getAudioTranscriptionBatch(batchId!),
    queryKey: batchId ? audioTranscriptionBatchQueryKey(batchId) : ['audio-transcription-batch', 'idle'],
    refetchInterval: (queryState) => resolveAudioTranscriptionPollInterval({
      dataUpdateCount: queryState.state.dataUpdateCount,
      fetchFailureCount: queryState.state.fetchFailureCount,
      status: queryState.state.data?.status,
    }),
  });

  useEffect(() => {
    didReconcileRef.current = false;
  }, [batchId]);

  useEffect(() => {
    const conversationId = query.data?.conversationId;
    const status = query.data?.status;
    if (!batchId || !conversationId || !isAudioTranscriptionTerminalStatus(status) || didReconcileRef.current) {
      return;
    }

    didReconcileRef.current = true;
    void reconcileConversationAfterTerminal(conversationId, queryClient).catch(() => {
      didReconcileRef.current = false;
    });
  }, [batchId, query.data?.conversationId, query.data?.status, queryClient]);

  const invalidate = () => {
    if (!batchId) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: audioTranscriptionBatchQueryKey(batchId) });
  };

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!batchId) {
        throw new Error('Lot de transcription manquant.');
      }
      return cancelAudioTranscriptionBatch(batchId);
    },
    onSuccess: invalidate,
  });

  const retryAnalysisMutation = useMutation({
    mutationFn: () => {
      if (!batchId) {
        throw new Error('Lot de transcription manquant.');
      }
      return retryAudioTranscriptionAnalysis(batchId);
    },
    onSuccess: invalidate,
  });

  const retryJobMutation = useMutation({
    mutationFn: (jobId: string) => {
      if (!batchId) {
        throw new Error('Lot de transcription manquant.');
      }
      return retryAudioTranscriptionJob(batchId, jobId);
    },
    onSuccess: invalidate,
  });

  return {
    cancel: cancelMutation.mutateAsync,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isPending: query.isPending,
    retryAnalysis: retryAnalysisMutation.mutateAsync,
    retryJob: retryJobMutation.mutateAsync,
    status: query.data ?? null,
  };
}

export function useAudioTranscript(input: {
  batchId?: string;
  enabled: boolean;
  jobId?: string;
}) {
  return useQuery({
    enabled: input.enabled && Boolean(input.batchId && input.jobId),
    queryFn: () => getAudioTranscript(input.batchId!, input.jobId!),
    queryKey: input.batchId && input.jobId
      ? audioTranscriptQueryKey(input.batchId, input.jobId)
      : ['audio-transcription-transcript', 'idle'],
  });
}
